<script lang="ts">
	import PostMeta from '$lib/components/PostMeta.svelte'
	import Link from '$lib/components/Link.svelte'
	import CommunitiesList from './CommunitiesList.svelte'
	import type { GeoApiResponse } from '$api/geo/+server'
	import type { CalendarResponse } from '$api/calendar/+server'
	import type { StyleSpecification } from 'maplibre-gl'
	import * as maplibregl from 'maplibre-gl'
	import 'maplibre-gl/dist/maplibre-gl.css'
	import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
	import { isMapboxURL, transformMapboxUrl } from 'maplibregl-mapbox-request-transformer'
	import { onDestroy, onMount } from 'svelte'
	import { communities, communitiesMeta } from './communities'
	import { MAPBOX_KEY } from './constants'
	import { HERO_ORANGE } from '$lib/colors'
	import escape from 'escape-html'

	// maplibre-gl v6 is ESM-only; the worker URL must be set explicitly under
	// bundlers like Vite (see v5→v6 migration guide).
	maplibregl.setWorkerUrl(workerUrl)

	// maplibre-gl doesn't support named imports on the server
	const { GeolocateControl, Map, Marker, Popup } = maplibregl

	const LOCATED_ZOOM = 4
	const STYLE_URL =
		'https://api.mapbox.com/styles/v1/mapbox/outdoors-v11?access_token=' + MAPBOX_KEY

	let { title, description, date } = communitiesMeta

	let map: maplibregl.Map
	let mapContainer: HTMLDivElement
	let lng: number = $state(-71.224518)
	let lat: number = $state(42.213995)
	let zoom: number = $state(1)

	function updateData() {
		zoom = map.getZoom()
		lng = map.getCenter().lng
		lat = map.getCenter().lat
	}

	async function fetchEvents() {
		try {
			const response = await fetch('/api/calendar?days=30')
			if (response.ok) {
				const data = (await response.json()) as CalendarResponse
				return data.entries.map((entry) => entry.event)
			}
			console.error('Failed to fetch events:', response.statusText)
		} catch (error) {
			console.error('Error fetching events:', error)
		}
		return []
	}

	async function fetchUserLocation() {
		try {
			const response = await fetch('/api/geo')
			if (response.ok) {
				const geoData = (await response.json()) as GeoApiResponse
				return {
					userLng: geoData.longitude,
					userLat: geoData.latitude
				}
			} else {
				console.error('Failed to fetch user location:', response.statusText)
			}
		} catch (error) {
			console.error('Error fetching user location:', error)
		}
		return {}
	}

	onMount(async () => {
		// Required, can throw
		const style = (await fetch(STYLE_URL).then((res) => res.json())) as StyleSpecification
		if (!style) return

		// Optional, call with error handling
		const [{ userLng, userLat }, events] = await Promise.all([fetchUserLocation(), fetchEvents()])

		const initialState = {
			lng: userLng ?? lng,
			lat: userLat ?? lat,
			zoom: userLat != null && userLng != null ? LOCATED_ZOOM : zoom
		}

		map = new Map({
			container: mapContainer,
			style: {
				...style,
				projection: {
					type: 'globe'
				}
			},
			center: [initialState.lng, initialState.lat],
			zoom: initialState.zoom,
			transformRequest: (url, resourceType) => {
				if (isMapboxURL(url)) return transformMapboxUrl(url, resourceType, MAPBOX_KEY)
			}
		})

		map.addControl(
			new GeolocateControl({
				fitBoundsOptions: {
					maxZoom: 4
				},
				positionOptions: {
					enableHighAccuracy: true
				},
				// When active the map will receive updates to the device's location as it changes.
				trackUserLocation: true
			})
		)

		map.on('move', () => {
			updateData()
		})

		map.on('load', () => {
			communities.map((community) => {
				new Marker({
					color:
						community.type === 'adjacent'
							? 'rgba(0,0,0,.5)'
							: community.type === 'national'
								? 'rgb(0, 150, 255)'
								: HERO_ORANGE,
					opacityWhenCovered: '0'
				})
					.setPopup(
						new Popup({ offset: [0, -15] }).setHTML(
							`<h3><a href="${community.link || 'https://discord.gg/CR5u5BTBwy'}">${
								community.name
							}</a></h3>`
						)
					)
					.setLngLat([community.lon, community.lat])
					.addTo(map)
			})

			// Sort descending so earlier events' markers are added last and render on top
			events
				.filter((event) => event.geo_latitude != null && event.geo_longitude != null)
				.sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime())
				.forEach((event) => {
					new Marker({
						color: 'var(--event-marker)',
						opacityWhenCovered: '0'
					})
						.setLngLat([event.geo_longitude!, event.geo_latitude!])
						.setPopup(
							new Popup({ offset: [0, -15] }).setHTML(
								`<h3><a href="${escape(`https://lu.ma/${event.url}`)}">${escape(event.name)}</a></h3>` +
									`<p>${new Intl.DateTimeFormat('en', { day: 'numeric', month: 'long' }).format(new Date(event.start_at))}</p>`
							)
						)
						.addTo(map)
				})
		})
	})

	onDestroy(() => {
		map?.remove()
	})
</script>

<PostMeta {title} {description} {date} />

<h1>{title}</h1>
<p>
	PauseAI Global has chapters and communities across the world. These communities are groups of
	individuals that care about the future and agree a <Link href="/proposal"
		>pause is the solution</Link
	>. They work together to educate members of the public and their political representatives about
	the <Link href="/risks">risks</Link>.
</p>

<p>
	If you're looking for a group closer to home, check out our map below to find the people nearest
	to you. The map also includes adjacent AI Safety communities in grey, and upcoming events in
	green.
</p>
<div>
	<div class="map-wrap">
		<div class="map" bind:this={mapContainer}></div>
	</div>
</div>
<CommunitiesList {communities} />

<p>
	Can't find a community near you and want to lead the way? Learn how to do it <Link
		href="/national-groups#how-to-set-up-a-pauseai-national-group">here</Link
	>.
</p>

<h2 id="events">Events</h2>

<p>Interested in attending a PauseAI community event? Find one below.</p>

<iframe
	src="https://lu.ma/embed/calendar/cal-E1qhLPs5IvlQr8S/events?"
	height="450"
	frameborder="0"
	style="border: 1px solid var(--border-luma-embed); border-radius: 24px; width: 100%;"
	allowfullscreen
	aria-hidden="false"
	title="PauseAI Events Calendar"
></iframe>

<p>
	Find the full list of events <Link href="https://lu.ma/PauseAI">here</Link>.
</p>

<p>
	If you want to organize an event, please create an event on Luma and press the "submit event"
	button on <Link href="https://lu.ma/PauseAI">our calendar page</Link>.
</p>

<style>
	.map-wrap {
		position: relative;
		padding-bottom: 56.25%; /* 16:9 */
		overflow: hidden;
		border-radius: 24px;
		border: 1px solid var(--text-subtle);
	}

	.map {
		position: absolute;
		width: 100%;
		height: 100%;
	}

	:global(.maplibregl-ctrl-group) {
		border-radius: 24px !important;
		border: 1px solid var(--text-subtle) !important;
		overflow: hidden;
	}

	:global(.maplibregl-ctrl-group button) {
		border-radius: 0 !important;
	}

	:global(.maplibregl-ctrl-group button:first-child) {
		border-top-left-radius: 24px !important;
		border-top-right-radius: 24px !important;
	}

	:global(.maplibregl-ctrl-group button:last-child) {
		border-bottom-left-radius: 24px !important;
		border-bottom-right-radius: 24px !important;
	}

	/* Maplibre popups stay white for contrast against the dark-mode map; force dark text on it */
	:global(.maplibregl-popup-content) {
		color: var(--grey-500);
		text-align: center;
	}

	:global(.maplibregl-popup-content p) {
		font-family: var(--font-body);
	}
</style>
